import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getVisibleQuestions,
  type OrgType,
  type Question,
  type QuestionId,
  type QuestionnaireAnswers,
} from '@shared';

interface UseQuestionnaireNavigationOptions {
  advisorId: string;
  visibleQuestions: Question[];
  orgType: OrgType | null;
  organizationName: string | null;
  isEdjPilotAdvisor: boolean;
  isDistributionPartnersRestricted: boolean;
}

export function useQuestionnaireNavigation({
  advisorId,
  visibleQuestions,
  orgType,
  organizationName,
  isEdjPilotAdvisor,
  isDistributionPartnersRestricted,
}: UseQuestionnaireNavigationOptions) {
  const navigate = useNavigate();

  const basePath = `/advisors/${advisorId}/onboarding/questionnaire`;

  const goToIntro = useCallback(() => navigate(basePath), [navigate, basePath]);
  const goToFinal = useCallback(() => navigate(`${basePath}/final`), [navigate, basePath]);

  const goToQuestion = useCallback(
    (questionId: QuestionId) => navigate(`${basePath}/${questionId}`),
    [navigate, basePath],
  );

  const goToBasicInfo = useCallback(() => {
    navigate(`/advisors/${advisorId}/onboarding/basic-info`);
  }, [navigate, advisorId]);

  const goToActivateSeat = useCallback(() => {
    navigate(`/advisors/${advisorId}/onboarding/activate-seat`);
  }, [navigate, advisorId]);

  const goNextFrom = useCallback(
    (currentId: QuestionId, answersForBranching: QuestionnaireAnswers) => {
      // Recompute visible questions from the just-submitted answers — branching
      // can reveal questions that weren't in the closure's stale list.
      const fresh = getVisibleQuestions({
        answers: answersForBranching,
        orgType,
        organizationName,
        isEdjPilotAdvisor,
        isDistributionPartnersRestricted,
      });
      const idx = fresh.findIndex(q => q.id === currentId);

      if (idx >= 0 && idx < fresh.length - 1) {
        goToQuestion(fresh[idx + 1].id);
        return;
      }

      goToFinal();
    },
    [
      orgType,
      organizationName,
      isEdjPilotAdvisor,
      isDistributionPartnersRestricted,
      goToQuestion,
      goToFinal,
    ],
  );

  const goPrevFrom = useCallback(
    (currentId: QuestionId | null) => {
      if (currentId == null) {
        goToBasicInfo();
        return;
      }

      const idx = visibleQuestions.findIndex(q => q.id === currentId);

      if (idx <= 0) {
        goToBasicInfo();
        return;
      }

      goToQuestion(visibleQuestions[idx - 1].id);
    },
    [visibleQuestions, goToBasicInfo, goToQuestion],
  );

  return {
    basePath,
    goToIntro,
    goToFinal,
    goToQuestion,
    goToBasicInfo,
    goToActivateSeat,
    goNextFrom,
    goPrevFrom,
  };
}
